import React from 'react';
import { 
  Home, 
  Users, 
  Calendar, 
  Eye, 
  BarChart3, 
  MessageSquare, 
  Database, 
  Settings,
  Building2,
  LogOut,
  Plus
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import MobileNavigation from './MobileNavigation';
import { PermissionGuard, Permission } from './PermissionGuard';
import NotificationBell from './NotificationBell';

/**
 * Format role value for display (e.g., "super_admin" -> "Super Admin")
 */
const formatRoleDisplay = (role) => {
  if (!role) return '';
  return role.split('_').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');
};

/**
 * Navigation items with permission requirements
 * Requirements: 7.2 - Frontend displays only menu items user has permission to access
 */
const sidebarItemsConfig = [
  { id: 'dashboard', label: 'Dashboard', icon: Home, permission: null }, // Dashboard accessible to all authenticated users
  { id: 'employees', label: 'Employees', icon: Users, permission: Permission.VIEW_EMPLOYEES },
  { id: 'performance', label: 'Performance', icon: BarChart3, permission: Permission.VIEW_REPORTS },
  { id: 'absences', label: 'Absences', icon: Calendar, permission: Permission.VIEW_ABSENCES },
  { id: 'observations', label: 'Observations', icon: Eye, permission: Permission.VIEW_OBSERVATIONS },
  { id: 'aiChat', label: 'AI Assistant', icon: MessageSquare, permission: null }, // AI Assistant accessible to all
  { id: 'dataSync', label: 'Data Sync', icon: Database, permission: Permission.MANAGE_INTEGRATIONS },
  { id: 'settings', label: 'Settings', icon: Settings, permission: Permission.MANAGE_SYSTEM }
];

const ResponsiveLayout = ({ 
  currentView, 
  setCurrentView, 
  currentUser, 
  userRoles, 
  handleLogout, 
  unreadCount,
  handleQuickAction,
  children 
}) => {

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col lg:flex-row">
      {/* Mobile Navigation */}
      <MobileNavigation
        currentView={currentView}
        setCurrentView={setCurrentView}
        currentUser={currentUser}
        userRoles={userRoles}
        handleLogout={handleLogout}
        unreadCount={unreadCount}
      />

      {/* Desktop Sidebar - Hidden on mobile */}
      <aside 
        className="hidden lg:flex lg:w-64 bg-black text-white flex-col"
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="p-6 border-b border-gray-800">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-lg flex items-center justify-center">
              <Building2 className="w-6 h-6 text-black" />
            </div>
            <div>
              <h1 className="text-xl font-bold">PeoplePulse</h1>
              <p className="text-xs text-gray-400">HR Management</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-2" role="navigation" aria-label="Primary navigation">
          {sidebarItemsConfig.map((item) => {
            const Icon = item.icon;
            const navButton = (
              <button
                key={item.id}
                onClick={() => setCurrentView(item.id)}
                className={`w-full flex items-center px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500 focus-visible:ring-offset-2 focus-visible:ring-offset-black ${
                  currentView === item.id 
                    ? 'bg-yellow-500 text-black shadow-lg' 
                    : 'text-gray-300 hover:text-white hover:bg-gray-800'
                }`}
                aria-current={currentView === item.id ? 'page' : undefined}
                aria-label={`Navigate to ${item.label}`}
              >
                <Icon className="w-5 h-5 mr-3" aria-hidden="true" />
                {item.label}
              </button>
            );

            // Wrap with PermissionGuard if permission is required
            // Requirements: 7.2 - Display only menu items user has permission to access
            if (item.permission) {
              return (
                <PermissionGuard key={item.id} permission={item.permission}>
                  {navButton}
                </PermissionGuard>
              );
            }
            return <React.Fragment key={item.id}>{navButton}</React.Fragment>;
          })}
        </nav>

        <div className="p-4 border-t border-gray-800" role="contentinfo">
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center">
              <span className="text-sm font-semibold text-yellow-400">
                {currentUser?.name?.charAt(0) || 'U'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{currentUser?.name}</p>
              <p className="text-xs text-gray-400 truncate">{userRoles.map(formatRoleDisplay).join(', ') || 'Staff'}</p>
            </div>
          </div>
          <Button 
            onClick={handleLogout} 
            variant="outline" 
            size="sm" 
            className="w-full border-gray-600 text-gray-300 hover:text-white hover:bg-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500 focus-visible:ring-offset-2"
            aria-label="Sign out of PeoplePulse"
          >
            <LogOut className="w-4 h-4 mr-2" aria-hidden="true" /> Sign Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Desktop Header - Hidden on mobile */}
        <header className="hidden lg:block bg-white border-b border-gray-200 px-4 sm:px-6 py-4" role="banner">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 capitalize">{currentView}</h2>
              <p className="text-sm text-gray-500">Memphis Business Academy • {new Date().toLocaleDateString()}</p>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-4">
              <NotificationBell className="hidden lg:block" />
              <Button 
                className="bg-yellow-500 hover:bg-yellow-600 text-black text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-700 focus-visible:ring-offset-2"
                onClick={() => handleQuickAction('new_hire')}
                aria-label="Quick actions menu"
              >
                <Plus className="w-4 h-4 mr-2" aria-hidden="true" /> 
                <span className="hidden sm:inline">Quick Action</span>
                <span className="sm:hidden">Action</span>
              </Button>
            </div>
          </div>
        </header>

        {/* Content Area */}
        <main 
          id="main-content"
          className="flex-1 p-4 sm:p-6 overflow-y-auto" 
          role="main"
          aria-label={`${currentView} content`}
        >
          {children}
        </main>
      </div>
    </div>
  );
};

export default ResponsiveLayout;